import React, { useEffect } from "react";
import { Button, IconButton } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { SchemaFields } from "./FieldRenderer";
import {
  sectionsForArea,
  fieldsForArea,
  areFieldsComplete,
} from "./specSheetConfig";

const WallSection = (props) => {
  const basicSections = sectionsForArea(props.config, "walls");
  const basicFields = fieldsForArea(props.config, "walls");
  const levelSections = sectionsForArea(props.config, "walls_levels");
  const levelFields = fieldsForArea(props.config, "walls_levels");

  const emptyLevel = () =>
    Object.fromEntries(levelFields.map((f) => [f.id, ""]));

  const handleChange = (fieldId, value) => {
    props.setFormValues({ ...props.formValues, [fieldId]: value });
  };

  useEffect(() => {
    const basicsComplete = areFieldsComplete(basicFields, props.formValues);
    const levelsComplete = (props.formValues.additionalWallInfo || []).every(
      (level) => areFieldsComplete(levelFields, level)
    );
    props.setIsComplete(basicsComplete && levelsComplete);
  }, [props.formValues, props.config, props.setIsComplete]);

  // Ensure at least one level exists in state
  useEffect(() => {
    if (
      !props.formValues.additionalWallInfo ||
      props.formValues.additionalWallInfo.length === 0
    ) {
      props.setFormValues({
        ...props.formValues,
        additionalWallInfo: [emptyLevel()],
      });
    }
  }, []);

  const handleAddLevel = () => {
    props.setFormValues({
      ...props.formValues,
      additionalWallInfo: [
        ...(props.formValues.additionalWallInfo || []),
        emptyLevel(),
      ],
    });
  };

  const handleRemoveLevel = (index) => {
    const newLevels = [...props.formValues.additionalWallInfo];
    newLevels.splice(index, 1);
    props.setFormValues({ ...props.formValues, additionalWallInfo: newLevels });
  };

  const handleLevelChange = (index) => (fieldId, value) => {
    const newLevels = [...(props.formValues.additionalWallInfo || [])];
    newLevels[index] = { ...newLevels[index], [fieldId]: value };
    props.setFormValues({ ...props.formValues, additionalWallInfo: newLevels });
  };

  return (
    <div>
      {basicSections.map((section) => (
        <div key={section.id} className="mb-8">
          <h3 className="app-muted mb-3 text-xs font-semibold uppercase tracking-wider">
            {section.title}
          </h3>
          <SchemaFields
            fields={section.fields}
            values={props.formValues}
            onChange={handleChange}
            columns={section.columns || 3}
          />
        </div>
      ))}

      {levelSections.map((section) => (
        <div key={section.id}>
          <h3 className="app-muted mb-3 text-xs font-semibold uppercase tracking-wider">
            {section.title}
          </h3>

          {(props.formValues.additionalWallInfo || []).map((level, index) => (
            <div
              key={index}
              className="app-surface mb-4 rounded-xl border p-4"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <p className="text-sm font-semibold tabular-nums">Level {index + 1}</p>
                {props.formValues.additionalWallInfo.length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveLevel(index)}
                    aria-label={`Delete level ${index + 1}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </div>

              <SchemaFields
                fields={section.fields}
                values={level}
                onChange={handleLevelChange(index)}
                columns={section.columns || 3}
              />
            </div>
          ))}

          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={handleAddLevel}
          >
            Add Level
          </Button>
        </div>
      ))}
    </div>
  );
};

export default WallSection;
